import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { powerPlantDetailAPI } from '../../services/api';
import { useSubscription } from '../../context/SubscriptionContext';
import UserPowerLocationList from './UserPowerLocationList';
import UserSummaryStats from './UserSummaryStats';
import './UserPowerLocationStatus.css';

const UserPowerLocationStatus = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { getSubscriptionForPlant } = useSubscription();
  const [userPowerLocations, setUserPowerLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [after, setAfter] = useState(null);
  const isInitialLoad = useRef(true);

  // 내 발전소 상세 목록 로드 (무한 스크롤)
  const loadUserPowerLocations = useCallback(async (reset = false) => {
    if (isLoading) return;
    
    console.log('loadUserPowerLocations called - reset:', reset, 'loading:', isLoading);
    setIsLoading(true);
    
    try {
      const condition = {
        cursor: reset ? null : cursor,
        after: reset ? null : after,
        sortBy: 'createdAt',
        sortDirection: 'DESC',
        limit: null,
      };
      
      console.log('API call condition:', condition);
      const response = await powerPlantDetailAPI.getDetailsList(condition);
      
      console.log('UserPowerLocationStatus - API response:', response);
      console.log('UserPowerLocationStatus - Power plants data:', response.data);
      
      // API 데이터를 기존 형식으로 변환
      const transformedData = response.data.map(plant => ({
        id: plant.id,
        name: plant.name,
        region: plant.region || '서울',
        location: { lat: plant.latitude, lng: plant.longitude },
        status: plant.overallStatus.toLowerCase().replace('_error', '_error'),
        inverter: {
          total: plant.inverterStatus?.total || 0,
          working: plant.inverterStatus?.current || 0
        },
        environmentSensor: {
          total: plant.environmentalSensorStatus?.total || 0,
          working: plant.environmentalSensorStatus?.current || 0
        },
        connectionBox: {
          total: plant.connectionBoxStatus?.total || 0,
          working: plant.connectionBoxStatus?.current || 0
        },
        communication: {
          total: 1,
          working: plant.communicationStatus === '연결됨' ? 1 : 0,
          status: plant.communicationStatus === '연결됨' ? 'connected' : 'disconnected'
        },
        powerGeneration: plant.currentPowerGenerationKw,
        efficiency: plant.efficiencyPercentage,
        dailyGeneration: plant.todayPowerGenerationMwh,
        monthlyGeneration: plant.monthlyPowerGenerationMwh || 0,
        totalGeneration: plant.totalCumulativePowerMwh,
        inclinedIrradiance: plant.tiltedIrradiance,
        horizontalIrradiance: plant.horizontalIrradiance,
        ambientTemperature: plant.ambientTemperature,
        surfaceTemperature: plant.surfaceTemperature
      }));
      
      if (reset) {
        console.log('Resetting user power locations with:', transformedData);
        setUserPowerLocations(transformedData || []);
      } else {
        console.log('Adding to existing user power locations:', transformedData);
        setUserPowerLocations(prev => {
          const newData = [...prev, ...(transformedData || [])];
          console.log('New user power locations state:', newData);
          return newData;
        });
      }
      
      setHasMore(response.hasNext || false);
      setCursor(response.nextCursor || null);
      setAfter(response.createdAt || null);
    } catch (error) {
      console.error('내 발전소 목록 로드 실패:', error);
      alert('내 발전소 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [cursor, after, isLoading]);

  // 초기 데이터 로드 (한 번만 실행)
  useEffect(() => {
    if (isInitialLoad.current) {
      console.log('useEffect triggered - initial load');
      loadUserPowerLocations();
      isInitialLoad.current = false;
    }
  }, [loadUserPowerLocations]);

  // 더 많은 데이터 로드 (무한스크롤)
  const loadMore = () => {
    if (hasMore && !isLoading) {
      loadUserPowerLocations();
    }
  };


  const handlePowerLocationSelect = (powerLocation) => {
    console.log('Power location selected:', powerLocation);
    navigate(`/power-plant/${powerLocation.id}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'normal': return '#10B981';
      case 'partial_error': return '#F59E0B';
      case 'communication_error': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'normal': return '정상';
      case 'partial_error': return '부분 오류';
      case 'communication_error': return '통신 오류';
      default: return '알 수 없음';
    }
  };

  return (
    <div className="user-power-location-status">
      <div className="status-content">
        <div className="status-left-panel">
          <UserSummaryStats powerLocations={userPowerLocations} user={user} />
        </div>

        <div className="status-main-panel">
          <UserPowerLocationList
            powerLocations={userPowerLocations}
            selectedPowerLocation={null}
            onPowerLocationSelect={handlePowerLocationSelect}
            getStatusColor={getStatusColor}
            getStatusText={getStatusText}
            getSubscriptionForPlant={getSubscriptionForPlant}
            isLoading={isLoading}
            hasMore={hasMore}
            onLoadMore={loadMore}
          />
        </div>
      </div>
    </div>
  );
};

export default UserPowerLocationStatus;
